'use strict'

const EventEmitter = require('events')
const Sentry = require('@sentry/browser')
const { Integrations } = require('@sentry/tracing')
const { Severity } = require('@sentry/types')

class RFID extends EventEmitter {
  constructor(address, opt) {
    super()

    if (typeof address != 'string') {
      throw new Error(`Specified WebSocket server address '${address}' is not correct`)
    }

    this._socket = null
    this._lastTagMessageTime = null
    this._lastStatusMessage = {
      status: 'disconnect',
      source: 'JS Library',
      message: 'Connection is not initialised'
    }
    this._address = address
    this._autoReconnect = opt?.autoReconnect !== undefined ? opt.autoReconnect : true
    this._autoReconnectInterval = parseInt(opt?.autoReconnectInterval) || 1000
    this._rfidMessagesMinRate = parseInt(opt?.rfidMessagesMinRate) || 3000
    this._rfidMessagesMinRateInterval = parseInt(opt?.rfidMessagesMinRateInterval) || 3000
    this._sentryEnvironment = opt?.sentryEnvironment || undefined
    this._sentryDsn = opt?.sentryDsn || "https://696348cb17384412b520ba6a9576e4f2@o926465.ingest.sentry.io/5881185"

    if (opt?.connectOnCreate) {
      this.connect()
    }

    this._initCheckMessagesRateInterval(this._rfidMessagesMinRateInterval)
    this._initSentry(this._sentryEnvironment, this._sentryDsn)
  }

  connect() {
    let self = this

    if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(this._socket?.readyState)) {
      console.warn('WebSocket connection already exists')
      return
    } else {
      this._socket = new WebSocket(this._address)
    }

    this._socket.onopen = () => {
      this._emitStatusChange({
        status: 'connect',
        source: 'JS Library',
        message: 'WebSocket connection from browser to middleware is opened'
      })
    }

    this._socket.onmessage = event => {
      const message = JSON.parse(event.data)
      const data = message.data

      if (message.type === 'tags_list') {
        self.emit('message', data)
        this._lastTagMessageTime = Date.now()
      } else if (message.type === 'connection_status') {
        this._emitStatusChange({...data, source: 'Middleware'})
      }
    }

    this._socket.onclose = () => {
      console.log('[RFID] WebSocket connection closed')
      this._emitStatusChange({
        status: 'disconnect',
        source: 'JS Library',
        message: 'WebSocket connection from browser to middleware is closed'
      })

      if (self._autoReconnect) {
        console.log(`[RFID] Will reconnect to '${self._address}' in ${self._autoReconnectInterval} mills`)
        setTimeout(() => self.connect(), this._autoReconnectInterval)
      }
    }
  }

  disconnect() {
    if (this._socket) {
      this._socket.close()
      this._socket = null
    }
  }

  getStatus() {
    return this._lastStatusMessage
  }

  _emitStatusChange(data) {
    this.emit(data.status, data)
    this._lastStatusMessage = data
    Sentry.captureMessage(`${data.status.toUpperCase()}: ${data.message}, source: ${data.source}`, Severity.Debug)
  }

  _initCheckMessagesRateInterval(checkInterval) {
    setInterval(() => {
      const badRateStatus = {
        status: 'disconnect',
        source: 'JS Library',
        message: 'Bad messages rate received from middleware'
      }

      if (Date.now() - this._lastTagMessageTime > this._rfidMessagesMinRate) {
        if (this._lastStatusMessage?.status === 'connect') {
          this._emitStatusChange(badRateStatus)
        }
      } else if (JSON.stringify(this._lastStatusMessage) === JSON.stringify(badRateStatus)) {
        this._emitStatusChange({
          status: 'connect',
          source: 'JS Library',
          message: 'Messages rate from middleware is recovered'
        })
      }
    }, checkInterval)
  }

  _initSentry(environment, dsn) {
    if (environment) {
      Sentry.init({
        dsn,
        environment,
        integrations: [new Integrations.BrowserTracing()],
      });
    }
  }
}

module.exports = RFID